import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowsClockwise,
  CheckCircle,
  Warning,
  XCircle,
  Pulse,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  ok: {
    Icon: CheckCircle,
    label: "OK",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    ring: "border-emerald-200",
  },
  skip: {
    Icon: CheckCircle,
    label: "Skip",
    pill: "bg-slate-50 text-slate-500 border-slate-200",
    ring: "border-slate-200",
  },
  warn: {
    Icon: Warning,
    label: "WARN",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    ring: "border-amber-200",
  },
  fail: {
    Icon: XCircle,
    label: "FAIL",
    pill: "bg-red-50 text-red-700 border-red-200",
    ring: "border-red-200",
  },
};

function usd(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Admin → Daily Ops — every network, last-24h clicks + reports, Sync now.
 */
export default function AdminDailyOps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingKey, setSyncingKey] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const didFetch = useRef(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data: summary } = await API.get("/admin/daily-ops/summary");
      setData(summary);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load daily ops");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchSummary();
  }, [fetchSummary]);

  const triggerSync = async (key) => {
    setSyncingKey(key);
    try {
      const opts = key === "shopnomix" || key === "admedia" ? { timeout: 600000 } : { timeout: 180000 };
      await API.post(`/admin/networks/${key}/sync-now`, {}, opts);
      toast.success(`${key} synced`);
      await fetchSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${key} sync failed`);
    } finally {
      setSyncingKey(null);
    }
  };

  const triggerSyncAll = async () => {
    setSyncingAll(true);
    try {
      const { data: res } = await API.post("/admin/daily-ops/sync-all", {}, { timeout: 900000 });
      const failed = (res.results || []).filter((r) => !r.ok);
      if (failed.length) {
        toast.error(`Synced ${res.synced}/${res.total} — failed: ${failed.map((f) => f.network).join(", ")}`);
      } else {
        toast.success(`Synced all ${res.total} networks`);
      }
      await fetchSummary();
    } catch (err) {
      toast.error(formatApiError(err) || "Sync all failed");
    } finally {
      setSyncingAll(false);
    }
  };

  if (loading && !data) {
    return <div className="p-8 text-slate-400 text-sm">Loading daily ops…</div>;
  }

  const overall = data?.overall_status || "warn";
  const overallMeta = STATUS[overall] || STATUS.warn;
  const OverallIcon = overallMeta.Icon;
  const networks = data?.networks || [];
  const busy = syncingAll || Boolean(syncingKey);

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-daily-ops-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
            Operations
          </p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">
            Daily Ops
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Clicks and conversion reports for the last 24 hours. Sync a network, or pull all of them before the morning email.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading || busy}>
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={triggerSyncAll}
            disabled={busy}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="daily-ops-sync-all"
          >
            <ArrowsClockwise size={14} className={`mr-1.5 ${syncingAll ? "animate-spin" : ""}`} />
            {syncingAll ? "Syncing all…" : "Sync all networks"}
          </Button>
        </div>
      </div>

      <div
        className={`border rounded-md p-3 mb-4 flex items-center justify-between gap-4 ${overallMeta.ring}`}
        data-testid="daily-ops-overall"
      >
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border ${overallMeta.pill}`}>
            <OverallIcon size={16} weight="fill" />
            {overallMeta.label}
          </span>
          <span className="text-sm text-slate-600">
            {networks.filter((n) => n.health === "ok").length} ok ·{" "}
            {networks.filter((n) => n.health === "warn").length} warn ·{" "}
            {networks.filter((n) => n.health === "fail").length} fail
          </span>
        </div>
        <span className="text-xs text-slate-400">
          Last 24h · updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"}
        </span>
      </div>

      <section className="mb-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Pulse size={18} className="text-[#002FA7]" />
          Networks
        </h2>
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="daily-ops-networks-table">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-3 py-2 font-bold">Network</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-right px-3 py-2 font-bold">Clicks</th>
                <th className="text-right px-3 py-2 font-bold">Reports</th>
                <th className="text-right px-3 py-2 font-bold">Payout</th>
                <th className="text-left px-3 py-2 font-bold">Last sync</th>
                <th className="text-right px-3 py-2 font-bold"> </th>
              </tr>
            </thead>
            <tbody>
              {networks.map((n) => {
                const meta = STATUS[n.health] || STATUS.warn;
                const Icon = meta.Icon;
                const canSync = n.configured || n.manual_only;
                return (
                  <tr
                    key={n.key}
                    className="border-b border-slate-100 last:border-0"
                    data-testid={`daily-ops-network-${n.key}`}
                  >
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{n.label}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${meta.pill}`}>
                        <Icon size={10} weight="fill" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">
                      {(n.clicks_24h ?? 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">
                      {(n.conversions_24h ?? 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">
                      {usd(n.payout_24h)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">
                      <div>{n.last_sync_age || "never"}</div>
                      {n.last_sync_error && (
                        <div className="text-[10px] text-red-600 truncate max-w-[220px]" title={n.last_sync_error}>
                          {n.last_sync_error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={busy || !canSync}
                        onClick={() => triggerSync(n.key)}
                        data-testid={`daily-ops-sync-${n.key}`}
                      >
                        <ArrowsClockwise size={12} className={`mr-1 ${syncingKey === n.key ? "animate-spin" : ""}`} />
                        {syncingKey === n.key ? "Syncing…" : "Sync"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {(data?.sync_issues?.actionable_count > 0) && (
        <section className="mb-8" id="sync-issues" data-testid="daily-ops-sync-issues">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">
            Jobs still failing
          </h2>
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Job</th>
                  <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">What to do</th>
                </tr>
              </thead>
              <tbody>
                {(data.sync_issues.items || []).filter((i) => i.needs_action).map((issue) => (
                  <tr key={issue.component} className="border-b border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-mono font-semibold text-slate-900">{issue.component}</div>
                      <div className="text-[10px] text-red-600 mt-1 truncate max-w-md" title={issue.last_error}>
                        {issue.last_error}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{issue.guidance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">More</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {[
            ["/admin/reports", "Reports"],
            ["/admin/diagnostics", "Diagnostics"],
            ["/admin/brands", "Brands catalog"],
            ["/admin/brands-sync", "Impact Sync"],
          ].map(([href, label]) => (
            <Link key={href} to={href} className="text-[#002FA7] hover:underline">{label}</Link>
          ))}
        </div>
      </section>
    </div>
  );
}
